import React, { useCallback, useMemo, useState, type ChangeEvent, type RefObject } from 'react';
import Button from '@atlaskit/button/new';
import Form, {
	ErrorMessage,
	Field,
	FormFooter,
	FormHeader,
	FormSection,
	HelperMessage,
} from '@atlaskit/form';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import Modal, { ModalBody } from '@atlaskit/modal-dialog';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Select, { type ValueType as Value } from '@atlaskit/select';
import TextField from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { DONE, IN_PROGRESS } from '@atlassian/jira-common-constants/src/status-categories';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useThirdPartyState } from '../../../../../../../controllers/import-state';
import {
	useThirdPartyStatusCategories,
	useThirdPartyStatusMappingState,
} from '../../../../../../../controllers/import-state/selectors/third-party-status-mapping-state';
import { useUIState } from '../../../../../../../controllers/ui-state';
import messages from './messages';

type Option = {
	label: string;
	value: string;
};

export type AddStatusModalProps = {
	statusCategory?: string;
	sourceStatusName?: string;
	newStatusName?: string;
	focusRef?: RefObject<HTMLElement>;
	onClose: () => void;
};

export const AddStatusModal = ({
	onClose,
	statusCategory,
	sourceStatusName,
	newStatusName,
	focusRef,
}: AddStatusModalProps) => {
	const { formatMessage } = useIntl();
	const categoryStatusOptions = useCategoryStatusOptions();
	const [{ thirdPartyStatusMapping }, { setThirdPartyStatusOption }] = useThirdPartyState();

	const preSelectedCategory = useMemo(
		() => categoryStatusOptions.find((option) => option.value === statusCategory),
		[categoryStatusOptions, statusCategory],
	);
	const [statusName, setStatusName] = useState(newStatusName ?? '');
	const [statusNameError, setStatusNameError] = useState<string | null>(null);
	const [selectedStatusCategory, setSelectedStatusCategory] = useState<Value<Option>>(
		preSelectedCategory ?? null,
	);
	const [, { setThirdPartyStatusMapping }] = useThirdPartyStatusMappingState();
	const [, { setUIState }] = useUIState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const existingStatuses = useMemo(
		() =>
			(thirdPartyStatusMapping &&
				thirdPartyStatusMapping.availableJiraStatuses
					.concat(thirdPartyStatusMapping.statusOptions)
					.map((status) => status.name.toLocaleLowerCase())) ??
			[],
		[thirdPartyStatusMapping],
	);

	const onStatusCategoryChange = (selectedOption: Value<Option>) => {
		if (selectedOption) {
			setSelectedStatusCategory(selectedOption);
		}
	};

	const getStatusCategoryStyle = useCallback((category: string) => {
		switch (category) {
			case IN_PROGRESS:
				return inProgressStatus;
			case DONE:
				return doneStatus;
			default:
				return defaultStatus;
		}
	}, []);

	const formatOption = (option: Option) => (
		<Inline space="space.075" alignBlock="center">
			{/* eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage */}
			<Box xcss={[statusCategoryColorBase, getStatusCategoryStyle(option.value)]} />
			{option.label}
		</Inline>
	);

	const handleBlur = useCallback(() => {
		const matchedStatus = existingStatuses.filter(
			(existingStatus) => existingStatus === statusName.toLocaleLowerCase(),
		);
		if (matchedStatus.length > 0) {
			return setStatusNameError(formatMessage(messages.statusNameAlreadyExistsError));
		}
		if (statusName.trim().length === 0) {
			return setStatusNameError(formatMessage(messages.statusNameBlankError));
		}
		return setStatusNameError(null);
	}, [existingStatuses, formatMessage, statusName]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setStatusName(e.target.value);
	}, []);

	const handleClose = useCallback(() => {
		setStatusName('');
		setStatusNameError(null);
		onClose();
	}, [onClose]);

	const handleSubmit = useCallback(() => {
		if (
			statusName &&
			statusNameError === null &&
			sourceStatusName &&
			selectedStatusCategory &&
			selectedStatusCategory.value !== null
		) {
			setThirdPartyStatusOption({
				name: statusName,
				statusCategory: selectedStatusCategory.value,
			});

			setThirdPartyStatusMapping({
				sourceStatusName,
				mappedStatusName: statusName,
				statusCategory: selectedStatusCategory.value,
			});

			setUIState({ hasUnsavedStatusMappingChanges: true });

			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'statusItem added', {
				selectedStatus: statusName,
				sourceStatus: sourceStatusName,
			});

			handleClose();
		}
	}, [
		createAnalyticsEvent,
		handleClose,
		selectedStatusCategory,
		setThirdPartyStatusMapping,
		setThirdPartyStatusOption,
		setUIState,
		sourceStatusName,
		statusName,
		statusNameError,
	]);

	return (
		<ShortcutScope>
			<Modal
				onClose={handleClose}
				onCloseComplete={() => focusRef?.current?.focus()}
				width="small"
				label={formatMessage(messages.modalHeader)}
				shouldReturnFocus={false}
				testId="project-create-importer-upload-flow.ui.third-party-review-screen.status-mapping.status-mapping-content.status-table.status-selector.add-status-modal.modal"
			>
				<ModalBody>
					<Box xcss={formWrapperStyles}>
						<Form onSubmit={handleSubmit}>
							{({ formProps, submitting }) => (
								<form {...formProps}>
									<FormHeader title={formatMessage(messages.modalHeader)} />
									<FormSection>
										<Box xcss={fieldWrapperStyles}>
											<Field
												aria-required
												label={formatMessage(messages.statusNameLabel)}
												name="statusName"
												isRequired
											>
												{({ fieldProps }) => (
													<>
														<TextField
															{...fieldProps}
															id="project-create-importer-upload-flow.ui.third-party-review-screen.status-mapping.status-mapping-content.status-table.status-selector.add-status-modal.statusName-input"
															autoComplete="off"
															aria-label={formatMessage(messages.statusNameLabel)}
															placeholder={formatMessage(messages.statusNamePlaceholder)}
															maxLength={60}
															onBlur={handleBlur}
															onChange={handleChange}
															value={statusName}
														/>
														{statusNameError && <ErrorMessage>{statusNameError}</ErrorMessage>}
													</>
												)}
											</Field>
										</Box>
										<Box xcss={fieldWrapperStyles}>
											<Field<Value<Option>>
												aria-required
												label={formatMessage(messages.statusCategoryLabel)}
												name="statusCategory"
												isRequired
											>
												{({ fieldProps: { id, ...rest } }) => (
													<>
														<Select
															inputId="project-create-importer-upload-flow.ui.third-party-review-screen.status-mapping.status-mapping-content.status-table.status-selector.add-status-modal.statusCategory"
															{...rest}
															options={categoryStatusOptions}
															onChange={onStatusCategoryChange}
															value={selectedStatusCategory}
															formatOptionLabel={formatOption}
														/>
														<HelperMessage>
															{formatMessage(messages.statusCategoryHelper)}
														</HelperMessage>
													</>
												)}
											</Field>
										</Box>
										<Box xcss={sectionWrapper}>
											<Inline space="space.050">
												<EditorInfoIcon label="" />
												{formatMessage(messages.newStatusInfoMessage)}
											</Inline>
										</Box>
									</FormSection>
									<FormFooter>
										<Button appearance="subtle" onClick={handleClose}>
											{formatMessage(messages.cancelButton)}
										</Button>
										<Button appearance="primary" type="submit" isLoading={submitting}>
											{formatMessage(messages.addButton)}
										</Button>
									</FormFooter>
								</form>
							)}
						</Form>
					</Box>
				</ModalBody>
			</Modal>
		</ShortcutScope>
	);
};

const useCategoryStatusOptions = (): Option[] => {
	const [{ statusCategories }] = useThirdPartyStatusCategories();
	return statusCategories.map((category) => ({
		label: capitalizeFirstLetter(category.replace('_', ' ').toLocaleLowerCase()),
		value: category,
	}));
};

const capitalizeFirstLetter = (str?: string) =>
	(str != null && str.charAt(0).toUpperCase() + str.slice(1)) || '';

const formWrapperStyles = xcss({
	marginBlock: 'space.300',
});

const fieldWrapperStyles = xcss({
	paddingBottom: 'space.150',
});

const sectionWrapper = xcss({
	paddingTop: 'space.150',
	color: 'color.text.subtlest',
});

const statusCategoryColorBase = xcss({
	width: '16px',
	height: '16px',
	borderRadius: 'border.radius.050',
});

const doneStatus = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.icon.accent.green', colors.G300)}`,
	backgroundColor: 'color.background.success',
});

const inProgressStatus = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.icon.accent.blue', colors.B100)}`,
	backgroundColor: 'color.background.information',
});

const defaultStatus = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border.bold', colors.N70)}`,
	backgroundColor: 'color.background.neutral',
});
